import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { Nav, Accordion, Form, Button, Spinner } from "react-bootstrap";

function normalize(el) {
  return {
    id: el.id ?? el.Id,
    supplyId: el.supplyId ?? el.SupplyId,
    supplyExternalId: el.supplyExternalId ?? el.SupplyExternalId,
    supplyName: el.supplyName ?? el.SupplyName,
    supplyCreationTime: el.supplyCreationTime ?? el.SupplyCreationTime,
    orderId: el.orderId ?? el.OrderId,
    orderExternalId: el.orderExternalId ?? el.OrderExternalId,
    orderCreationTime: el.orderCreationTime ?? el.OrderCreationTime,
    article: el.article ?? el.Article,
    name: el.name ?? el.Name,
    image: el.image ?? el.Image,
    packageLabelId: el.packageLabelId ?? el.PackageLabelId,
    packageLabelPartA: el.packageLabelPartA ?? el.PackageLabelPartA,
    packageLabelPartB: el.packageLabelPartB ?? el.PackageLabelPartB,
    isCompleted: el.isCompleted ?? el.IsCompleted ?? false,
  };
}

const SupplyListTab = ({
  mode = "normal",
  onChangeMode,
  service,
  onChangeService,
  printerAccordion,
  printViaPrintServerBase64,
}) => {
  const [orderPositions, setOrderPositions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPositionIds, setSelectedPositionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [doingComplete, setDoingComplete] = useState(false);

  const unique = (arr) => Array.from(new Set(arr));

  // загрузка
  const loadPositions = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await api.get(`/packaging/assemblylist/${service}`, {});
      const list = (res.data?.orderPositions ?? res.data?.OrderPositions ?? []).map(normalize);
      const total = res.data?.totalCount ?? res.data?.TotalCount ?? list.length;
      setOrderPositions(list);
      setTotalCount(total);
      setSelectedPositionIds([]);
    } catch (e) {
      console.error(e);
      setErr(`Ошибка: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  // группировка по поставке
  const groupedBySupply = useMemo(() => {
    const map = new Map();
    for (const p of orderPositions) {
      const key = p.supplyId || "NO_SUPPLY";
      if (!map.has(key)) {
        map.set(key, {
          supplyId: p.supplyId || "NO_SUPPLY",
          supplyExternalId: p.supplyExternalId,
          supplyName: p.supplyName || "Без поставки",
          supplyCreationTime: p.supplyCreationTime,
          positions: [],
        });
      }
      map.get(key).positions.push(p);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.supplyId === "NO_SUPPLY") return 1;
      if (b.supplyId === "NO_SUPPLY") return -1;
      const at = a.supplyCreationTime ? new Date(a.supplyCreationTime).getTime() : 0;
      const bt = b.supplyCreationTime ? new Date(b.supplyCreationTime).getTime() : 0;
      return bt - at;
    });
  }, [orderPositions]);

  // Ozon: группировка по заказу
  const groupByOrder = (positions) => {
    const map = new Map();
    for (const p of positions) {
      const key = p.orderId || "unknown";
      if (!map.has(key)) {
        map.set(key, { orderId: key, orderExternalId: p.orderExternalId, positions: [] });
      }
      map.get(key).positions.push(p);
    }
    return Array.from(map.values());
  };

  // счётчик
  const completedCount = orderPositions.filter((p) => p.isCompleted).length;
  const headerCountLabel =
    mode === "packing"
      ? `(${completedCount}/${orderPositions.length}${
          totalCount && totalCount !== orderPositions.length ? ` из ${totalCount}` : ""
        })`
      : `(${selectedPositionIds.length}/${orderPositions.length}${
          totalCount && totalCount !== orderPositions.length ? ` из ${totalCount}` : ""
        })`;

  // обычный режим — выделение
  const toggleSupplyGroup = (group) => {
    if (mode === "packing") return;
    const ids = group.positions.map((p) => p.id);
    const allSelected = ids.every((id) => selectedPositionIds.includes(id));
    setSelectedPositionIds((prev) => {
      if (allSelected) return prev.filter((id) => !ids.includes(id));
      const add = ids.filter((id) => !prev.includes(id));
      return [...prev, ...add];
    });
  };

  const togglePosition = (p) => {
    if (mode === "packing") return;
    const id = p.id;
    setSelectedPositionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleOrderGroupSelection = (rows) => {
    if (mode === "packing") return;
    const ids = rows.map((r) => r.id);
    const allSelected = ids.every((id) => selectedPositionIds.includes(id));
    setSelectedPositionIds((prev) => {
      if (allSelected) return prev.filter((id) => !ids.includes(id));
      const add = ids.filter((id) => !prev.includes(id));
      return [...prev, ...add];
    });
  };

  const handleSelectAll = (e) => {
    if (mode === "packing") return;
    const checked = e.target.checked;
    if (!checked) return setSelectedPositionIds([]);
    setSelectedPositionIds(orderPositions.map((p) => p.id));
  };

  // скачать / печать (обычный режим)
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        const base64 = String(dataUrl).split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleDownloadLabels = async () => {
    try {
      if (mode !== "normal") return;
      if (selectedPositionIds.length === 0) return;
      const resp = await api.post(
        `/packaging/download/${service}`,
        { orderPositionIds: unique(selectedPositionIds).map(String) },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });

      const cd = resp.headers?.["content-disposition"] || "";
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
      const filename = decodeURIComponent(m?.[1] || m?.[2] || "labels.pdf");

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Ошибка скачивания этикеток:", e);
      setErr(`Ошибка скачивания: ${e.message}`);
    }
  };

  const handlePrintLabels = async () => {
    try {
      if (mode !== "normal") return;
      if (selectedPositionIds.length === 0) return;
      const resp = await api.post(
        `/packaging/download/${service}`,
        { orderPositionIds: unique(selectedPositionIds).map(String) },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const base64 = await blobToBase64(blob);
      await printViaPrintServerBase64(base64);
    } catch (e) {
      console.error("Ошибка печати этикеток:", e);
      setErr(`Ошибка печати: ${e.message}`);
    }
  };

  // упаковка — отметки
  const completePositions = async (ids) => {
    await api.post(`/packaging/complete/${service}`, { orderPositionIds: ids.map(String) });
    setOrderPositions((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, isCompleted: true } : p)));
  };

  const cancelPositions = async (ids) => {
    await api.post(`/packaging/cancel/${service}`, { orderPositionIds: ids.map(String) });
    setOrderPositions((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, isCompleted: false } : p)));
  };

  const handleToggleComplete = async (ids, currentlyCompleted) => {
    if (doingComplete) return;
    try {
      setDoingComplete(true);
      if (currentlyCompleted) {
        const ok = window.confirm("Снять отметку об упаковке?");
        if (!ok) return;
        await cancelPositions(ids);
      } else {
        await completePositions(ids);
      }
    } catch (e) {
      console.error(e);
      setErr(`Не удалось обновить статус упаковки: ${e.message}`);
    } finally {
      setDoingComplete(false);
    }
  };

  // ==== Рендер строк ====
  // WB — обычный: чекбокс слева, PartB скрыт
  const renderRowWBNormal = (p) => (
    <tr key={p.id}>
      <td style={{ width: 56 }}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectedPositionIds.includes(p.id)}
          onChange={() => togglePosition(p)}
        />
      </td>
      <td style={{ width: 120 }}>
        <img src={p.image} alt={p.article} style={{ width: 100, height: 100, objectFit: "contain" }} />
      </td>
      <td>
        <div>{p.name}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>{p.article}</div>
      </td>
      <td style={{ width: 300 }}>
        <div style={{ fontSize: 12, color: "#666" }}>{p.packageLabelPartA || "—"}</div>
      </td>
    </tr>
  );

  // WB — упаковка: PartB показан, чекбокс справа большой, зелёный
  const renderRowWBPacking = (p) => (
    <tr key={p.id}>
      <td className="px-0" style={{ width: 80 }}>
        <img src={p.image} alt={p.article} style={{ width: 80, height: 100, objectFit: "contain" }} />
      </td>
      <td style={{ width: 500 }}>
        <div>{p.name}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>{p.article}</div>
      </td>
      <td className="" style={{ width: 50 }}>
        <div style={{ fontSize: 12, color: "#666" }}>{p.packageLabelPartA || "—"}</div>
        <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: 2, marginTop: 4 }}>
          {p.packageLabelPartB || "—"}
        </div>
      </td>
      <td className="pe-3" style={{ width: 25 }}>
        <input
          type="checkbox"
          className="form-check-input"
          style={{ transform: "scale(2.3)", cursor: "pointer", accentColor: "#198754" }}
          checked={p.isCompleted}
          onChange={() => handleToggleComplete([p.id], p.isCompleted)}
          disabled={doingComplete}
          title={p.isCompleted ? "Упаковано" : "Не упаковано"}
        />
      </td>
    </tr>
  );

  // Ozon — обычный: чекбокс слева на заказ (rowSpan), PartB скрыт
  const renderOrderGroupOzonNormal = (orderGroup) => {
    const rows = orderGroup.positions;
    if (!rows.length) return null;
    const ids = rows.map((r) => r.id);
    const allSel = ids.every((id) => selectedPositionIds.includes(id));
    const someSel = !allSel && ids.some((id) => selectedPositionIds.includes(id));

    return rows.map((p, idx) => (
      <tr key={p.id}>
        {idx === 0 && (
          <td style={{ width: 56 }} rowSpan={rows.length}>
            <input
              type="checkbox"
              className="form-check-input"
              ref={(el) => { if (el) el.indeterminate = someSel; }}
              checked={allSel}
              onChange={() => toggleOrderGroupSelection(rows)}
            />
          </td>
        )}
        <td style={{ width: 120 }}>
          <img src={p.image} alt={p.article} style={{ width: 100, height: 100, objectFit: "contain" }} />
        </td>
        <td>
          <div>{p.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{p.article}</div>
        </td>
        {idx === 0 && (
          <td style={{ width: 300 }} rowSpan={rows.length}>
            <div style={{ fontSize: 12, color: "#666" }}>{p.packageLabelPartA || "—"}</div>
          </td>
        )}
      </tr>
    ));
  };

  // Ozon — упаковка: PartB показан, чекбокс справа большой, зелёный, на весь заказ
  const renderOrderGroupOzonPacking = (orderGroup) => {
    const rows = orderGroup.positions;
    if (!rows.length) return null;
    const ids = rows.map((r) => r.id);
    const allCompleted = rows.every((r) => r.isCompleted);

    return rows.map((p, idx) => (
      <tr key={p.id}>
        <td className="px-0" style={{ width: 80 }}>
          <img src={p.image} alt={p.article} style={{ width: 80, height: 100, objectFit: "contain" }} />
        </td>
        <td style={{ width: 500 }}>
          <div>{p.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{p.article}</div>
        </td>
        {idx === 0 && (
          <td style={{ width: 50 }} rowSpan={rows.length}>
            <div style={{ fontSize: 12, color: "#666" }}>{p.packageLabelPartA || "—"}</div>
            <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: 2, marginTop: 4 }}>
              {p.packageLabelPartB || "—"}
            </div>
          </td>
        )}
        {idx === 0 && (
          <td className="pe-3" style={{ width: 25 }} rowSpan={rows.length}>
            <input
              type="checkbox"
              className="form-check-input"
              style={{ transform: "scale(2.3)", cursor: "pointer", accentColor: "#198754" }}
              checked={allCompleted}
              onChange={() => handleToggleComplete(ids, allCompleted)}
              disabled={doingComplete}
              title={allCompleted ? "Упаковано (все позиции заказа)" : "Не упаковано"}
            />
          </td>
        )}
      </tr>
    ));
  };

  return (
    <div>
      {err && <p style={{ color: "red" }}>{err}</p>}

      {/* Переключатель marketplace */}
      <Nav variant="tabs" activeKey={service} onSelect={onChangeService}>
        <Nav.Item><Nav.Link eventKey="wb">WB</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="ozon">Ozon</Nav.Link></Nav.Item>
      </Nav>

      {/* Блок принтера (в обычном режиме виден, в упаковке скрыт на уровне родителя) */}
      <div className="mt-3">{printerAccordion}</div>

      {/* === Тумблер режимов — над панелью с кнопками === */}
      <div className="d-flex justify-content-end mb-2">
        <div className="btn-group" role="group" aria-label="Режим">
          <button
            type="button"
            className={`btn btn-sm ${mode === "normal" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => onChangeMode?.("normal")}
          >
            Печать
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === "packing" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => onChangeMode?.("packing")}
          >
            Упаковка
          </button>
        </div>
      </div>

      {/* Панель действий */}
      <div className="d-flex align-items-center gap-2 mb-3">
        {mode === "normal" && (
          <>
            <Form.Check
              type="checkbox"
              id="select-all"
              label={<span className="ms-2">Выделить все {headerCountLabel}</span>}
              checked={orderPositions.length > 0 && selectedPositionIds.length === orderPositions.length}
              onChange={handleSelectAll}
            />

            <Button variant="warning" onClick={handlePrintLabels} disabled={selectedPositionIds.length === 0}>
              Напечатать этикетки
            </Button>

            <Button variant="info" onClick={handleDownloadLabels} disabled={selectedPositionIds.length === 0}>
              Скачать этикетки
            </Button>
          </>
        )}

        <Button variant="outline-secondary" onClick={loadPositions} disabled={loading}>
          Обновить {mode === "packing" ? headerCountLabel : ""}
        </Button>
        {loading && <Spinner size="sm" className="ms-2" />}
      </div>

      {/* Аккордеоны по поставкам */}
      <Accordion alwaysOpen>
        {groupedBySupply.map((group, idx) => {
          const ids = group.positions.map((p) => p.id);
          const allSelected = ids.every((id) => selectedPositionIds.includes(id));
          const someSelected = !allSelected && ids.some((id) => selectedPositionIds.includes(id));
          const groupCompletedCount = group.positions.filter((p) => p.isCompleted).length;

          return (
            <Accordion.Item eventKey={String(idx)} key={`supply-${group.supplyId}-${idx}`}>
              <Accordion.Header>
                <div className="d-flex align-items-center w-100" style={{ gap: 12 }}>
                  {mode === "normal" && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      checked={allSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); toggleSupplyGroup(group); }}
                    />
                  )}
                  <div className="flex-grow-1">
                    <div><strong>{group.supplyName}</strong></div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {group.supplyCreationTime ? new Date(group.supplyCreationTime).toLocaleString() : "—"}
                    </div>
                  </div>
                  <span className="text-muted">
                    {mode === "packing"
                      ? `(${groupCompletedCount}/${ids.length})`
                      : `(${ids.filter((id) => selectedPositionIds.includes(id)).length}/${ids.length})`}
                  </span>
                </div>
              </Accordion.Header>

              <Accordion.Body className="px-0">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      {mode === "normal" && <th style={{ width: 56 }}>Выбрать</th>}
                      <th style={{ width: 80 }}>Картинка</th>
                      <th> Название / Артикул</th>
                      <th style={{ width: 80 }}>ШК</th>
                      {mode === "packing" && <th style={{ width: 26 }}></th>}
                    </tr>
                  </thead>

                  {service === "wb" ? (
                    <tbody>
                      {mode === "packing"
                        ? group.positions.map((p) => renderRowWBPacking(p))
                        : group.positions.map((p) => renderRowWBNormal(p))}
                    </tbody>
                  ) : (
                    <tbody>
                      {mode === "packing"
                        ? groupByOrder(group.positions).map((og) => renderOrderGroupOzonPacking(og))
                        : groupByOrder(group.positions).map((og) => renderOrderGroupOzonNormal(og))}
                    </tbody>
                  )}
                </table>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

export default SupplyListTab;
